/**
 * use-channel-picker.ts — Lọc danh sách tài khoản kênh theo NHÓM KÊNH + Ô TÌM.
 *
 * Ô chọn tài khoản ở Hội thoại và menu "Kênh áp dụng" ở Train AI trước đây
 * mỗi nơi tự viết một bản lọc; gom về đây để hai màn cư xử y hệt nhau.
 */
import { useMemo, useState } from 'react'
import { groupOfPlatform, groupsPresent } from '@/lib/channel-groups'
import type { ChannelFilterValue } from '@/components/shared/channel-chips'

/** Từ bao nhiêu tài khoản trở lên thì hiện ô tìm (ít hơn thì liếc là thấy). */
export const PICKER_SEARCH_THRESHOLD = 10

interface PickerItem {
  displayName?: string | null
  platform?: number | null
  phone?: string | null
}

export function useChannelPicker<T extends PickerItem>(items: T[]) {
  const [query, setQuery] = useState('')
  const [channel, setChannel] = useState<ChannelFilterValue>('all')

  const groups = useMemo(() => groupsPresent(items.map((i) => i.platform)), [items])
  // Nhóm đang chọn biến mất (vd gỡ tài khoản cuối cùng của nhóm) → coi như
  // "Tất cả", tránh kẹt ở danh sách rỗng mà không còn chip nào để bấm ra.
  const effective: ChannelFilterValue =
    channel !== 'all' && groups.some((g) => g.id === channel) ? channel : 'all'

  const matched = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((i) => {
      if (effective !== 'all' && groupOfPlatform(i.platform) !== effective) return false
      if (!q) return true
      return (i.displayName ?? '').toLowerCase().includes(q) || (i.phone ?? '').includes(q)
    })
  }, [items, query, effective])

  return {
    query,
    setQuery,
    channel: effective,
    setChannel,
    groups,
    matched,
    /** Chỉ hiện chip khi có từ 2 nhóm — một nhóm thì chip "lọc" chẳng lọc gì. */
    showChips: groups.length > 1,
    showSearch: items.length > PICKER_SEARCH_THRESHOLD,
  }
}
